import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';
import { shouldDeferCartSideEffects } from '@theme/cart-add-perf';

/** Shop Pay installments #shopify-installments-cta: close mini cart (one composedPath pass; early exit when drawer found). */
function closeOpenCartDrawerFromEvent(/** @type {Event} */ event) {
  const path = event.composedPath?.() ?? [];
  let sawCta = false;

  for (let i = 0; i < path.length; i++) {
    const node = path[i];
    if (!(node instanceof Element)) continue;

    if (
      node.id === 'shopify-installments-cta' ||
      node.getAttribute('data-testid') === 'shopify-installments-cta'
    ) {
      sawCta = true;
    }

    if (
      sawCta &&
      node.tagName === 'CART-DRAWER-COMPONENT' &&
      node.querySelector('dialog')?.open
    ) {
      /** @type {HTMLElement & { close: () => void }} */ (node).close();
      return;
    }
  }

  if (!sawCta) return;

  for (const el of document.querySelectorAll('cart-drawer-component')) {
    if (el.querySelector('dialog')?.open) {
      /** @type {HTMLElement & { close: () => void }} */ (el).close();
      return;
    }
  }
}

document.addEventListener('click', closeOpenCartDrawerFromEvent, { capture: true });

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    if (CartDrawerComponent.#isSafariBrowser()) {
      this.classList.add('cart-drawer--safari-thumbs');
    }
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.addEventListener('click', this.handleContinueShoppingClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.removeEventListener('click', this.handleContinueShoppingClick);
  }

  /**
   * True for Apple Safari (macOS / iOS). False for Chrome/Firefox/Edge — including iOS variants.
   * @returns {boolean}
   */
  static #isSafariBrowser() {
    const ua = navigator.userAgent || '';
    const vendor = navigator.vendor || '';
    if (!vendor.includes('Apple')) return false;
    return !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  }

  /**
   * Theme setting "Load theme scripts when first needed" on: a cart:update that happened before
   * this file loaded (the cart group in snippets/scripts.liquid) is handed here once the drawer is
   * defined, so the drawer opens as it would have. Only this element's handler sees it again.
   * @param {Event} event
   */
  catchUpCartUpdate(event) {
    this.#handleCartUpdate(event);
  }

  /**
   * Re-open drawer after cart line updates so morphSection keeps the dialog visible.
   * cart:update is dispatched by cart-items-component after section re-render with itemCount.
   */
  #handleCartUpdate = (/** @type {any} */ event) => {
    const source = event.detail?.data?.source;
    // Rising Auctions cart cleanup runs unattended, so it must not open the sliding mini-cart.
    // TWS-1070: Pay Now add-to-bag keeps the shopper on the page, so it must not open it either.
    if (source === 'rising-auctions-cart-cleanup' || source === 'ra-pay-now') {
      return;
    }
    // Shared-cart login restore: update bubble only; do not open drawer or redirect to /cart.
    if (typeof source === 'string' && source.startsWith('shared-cart-sync')) {
      return;
    }

    const path = window.location.pathname || '';
    if (path === '/cart' || /\/cart$/.test(path)) {
      return;
    }

    // TWS-1070: On Pay Now, never auto-open the drawer after Add to Bag. Follow-up events
    // (e.g. cart-items-component free-gift morph) would otherwise reopen it. When the shopper
    // already opened the bag via the header icon, still call open() so the morph keeps it visible.
    if (document.querySelector('[data-pay-now]') && !this.querySelector('dialog')?.open) {
      return;
    }

    // Run after cart-items-component morphs the mini-cart section on the same event.
    queueMicrotask(() => {
      void this.open(event);
    });
  };

  async open(/** @type {any} */ event) {
    if (shouldDeferCartSideEffects(event)) return;

    const cartItems = /** @type {HTMLElement | null} */ (this.querySelector('cart-items-component'));
    const sectionId = cartItems?.dataset?.sectionId;
    const detail = /** @type {any} */ (event)?.detail;
    const data = detail?.data;
    const cartPageMorphSources = new Set([
      'tjc-membership',
      'tjc-promo',
      'main-cart-bp-sync',
      'cart-section-refresh',
      'cart-items-component-bp',
      'cart-page-budget-pay-choice',
    ]);
    const isNotAllowedSource =
      data?.source &&
      data.source !== 'product-form-component' &&
      data.source !== 'quick-add' &&
      data.source !== 'iw-stepper-lp' &&
      data.source !== 'cart-items-component' &&
      !cartPageMorphSources.has(data.source);

    if (isNotAllowedSource) {
      window.location.href = Theme.routes.cart_url;
      return;
    }

    try {
      const risingAuctions = /** @type {Window & { RA?: { cleanupExpiredAuctionCartLines?: (options?: { sourceId?: string }) => Promise<unknown> } }} */ (window).RA;
      if (sectionId && risingAuctions && typeof risingAuctions.cleanupExpiredAuctionCartLines === 'function') {
        await risingAuctions.cleanupExpiredAuctionCartLines({ sourceId: sectionId });
      }
    } catch (_) {
      // Still open drawer if RA cleanup fails
    }

    // Section HTML is morphed by cart-items-component on cart:update — do not fetch again here
    // (a second renderSection aborts in-flight Rebuy product-card section requests).
    if (this.querySelector('.cart-drawer__summary')) {
      // Adding an item may reopen the cart while add-to-bag is still animating closed.
      const addToBag = document.querySelector('add-to-bag-modal');
      if (addToBag?.querySelector('dialog')?.open) {
        await addToBag.closeDialog();
      }
      this.showDialog();
      // showDialog opens in rAF — preload after paint so Safari re-evaluates drawer imgs.
      requestAnimationFrame(() => {
        this.#preloadThumbnails();
      });
      document.dispatchEvent(new CustomEvent('cart-drawer:open'));
    }
  }

  /**
   * Force drawer images to load after open / post-morph (Safari dialog + loading=lazy).
   */
  #preloadThumbnails = () => {
    this.querySelectorAll('img[loading="lazy"]').forEach((image) => {
      image.removeAttribute('loading');
    });
  };

  close() {
    this.closeDialog();
  }
  handleContinueShoppingClick = (/** @type {any} */ event) => {
    if (event.target.classList.contains('cart-items__empty-button') || event.target.closest('.cart-items__empty-button')) {
      this.closeDialog();
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
